/** VISTOERA_COLLAPSED_GENERATED_MEDIA_FRAME_TRUE_RESULT_HUGGING_ALIGN_WITH_IMAGE_ASSET_CANONICAL_REFERENCE_P0_V2
 *
 * Presentation-type-aware collapsed sizing:
 * - MEDIA (image/video): outer frame hugs displayed media like ImageAsset — no generic header min, no card inset.
 * - TEXT / STRUCTURED: keep generation-card minimums and insets.
 * - AUDIO: audio-specific width cap.
 * Expanded layout width stays on the canonical generation preset. */

import { generationNodeCardWidth } from './generationNodeWidthPresets'
import { computeMediaBoxSize, computeCollapsedImageDisplaySize } from './mediaBoxSize'
import { IMAGE_SUCCESS_RESULT_MAX_HEIGHT_COLLAPSED } from './imageGenerationResultLayout'

/** @deprecated Use TEXT_COLLAPSED_HEADER_MIN_WIDTH_PX — kept for legacy imports/tests. */
export const COLLAPSED_RESULT_HEADER_MIN_WIDTH_PX = 200

/** @deprecated Use TEXT_COLLAPSED_INSET_X_PX — kept for legacy imports/tests. */
export const COLLAPSED_RESULT_INSET_X_PX = 4

/** Text / structured collapsed cards — protects readable synopsis rows. */
export const TEXT_COLLAPSED_HEADER_MIN_WIDTH_PX = 200
export const TEXT_COLLAPSED_INSET_X_PX = 4

/** Media collapsed cards — matches ImageAsset `computeMediaBoxSize` MIN_DIM. */
export const MEDIA_COLLAPSED_MIN_WIDTH_PX = 150

/** Media collapsed result — zero horizontal inset (ImageAsset reference). */
export const MEDIA_COLLAPSED_HORIZONTAL_INSET_PX = 0

/** Hard clamp shared with mediaBoxSize HARD_MAX. */
export const COLLAPSED_RESULT_MAX_WIDTH_PX = 460

/** Canonical empty collapsed placeholder width when no artifact exists yet. */
export const COLLAPSED_RESULT_EMPTY_WIDTH_PX = 260

export type CollapsedResultStrategy = 'image' | 'video' | 'audio' | 'text' | 'structured'

export interface ResolveGenerationCardWidthParams {
  expanded: boolean
  expandedWidthPx: number
  hasGeneratedResult: boolean
  strategy: CollapsedResultStrategy
  naturalWidth?: number
  naturalHeight?: number
  aspectRatio?: string
  resultCarrierWidthPx?: number
}

function ratioToNaturalSize(aspectRatio: string | undefined): { w: number; h: number } {
  const [wRaw, hRaw] = (aspectRatio || '1:1').split(':')
  const w = Math.max(1, Number(wRaw) || 1)
  const h = Math.max(1, Number(hRaw) || 1)
  return { w: w * 100, h: h * 100 }
}

/** ImageAsset-canonical media box for collapsed generated media results. */
export function resolveCollapsedMediaPresentationSize(
  naturalWidth: number,
  naturalHeight: number,
): { mediaWidth: number; mediaHeight: number } {
  const box = computeMediaBoxSize(naturalWidth, naturalHeight)
  return { mediaWidth: box.width, mediaHeight: box.height }
}

/** Back-compat alias — carrier == media presentation boundary. */
export function resolveCollapsedMediaCarrierSize(
  naturalWidth: number,
  naturalHeight: number,
  options?: { maxHeight?: number },
): { carrierWidth: number; carrierHeight: number } {
  void options
  const { mediaWidth, mediaHeight } = resolveCollapsedMediaPresentationSize(naturalWidth, naturalHeight)
  return { carrierWidth: mediaWidth, carrierHeight: mediaHeight }
}

function resolveMediaCollapsedCardWidth(mediaWidth: number): number {
  return Math.min(
    COLLAPSED_RESULT_MAX_WIDTH_PX,
    Math.max(MEDIA_COLLAPSED_MIN_WIDTH_PX, mediaWidth),
  )
}

function resolveTextCollapsedCardWidth(contentWidth: number, expandedWidthPx: number): number {
  const withInset = contentWidth + TEXT_COLLAPSED_INSET_X_PX * 2
  return Math.min(
    COLLAPSED_RESULT_MAX_WIDTH_PX,
    Math.max(TEXT_COLLAPSED_HEADER_MIN_WIDTH_PX, withInset, Math.min(expandedWidthPx, COLLAPSED_RESULT_MAX_WIDTH_PX)),
  )
}

export function resolveCollapsedResultCardWidth(params: {
  hasGeneratedResult: boolean
  strategy: CollapsedResultStrategy
  naturalWidth?: number
  naturalHeight?: number
  aspectRatio?: string
  resultCarrierWidthPx?: number
  expandedWidthPx: number
}): number {
  if (!params.hasGeneratedResult) {
    if (params.strategy === 'image' || params.strategy === 'video') {
      const { w, h } =
        params.naturalWidth && params.naturalHeight
          ? { w: params.naturalWidth, h: params.naturalHeight }
          : ratioToNaturalSize(params.aspectRatio)
      return computeCollapsedImageDisplaySize(w, h, {
        maxHeight: IMAGE_SUCCESS_RESULT_MAX_HEIGHT_COLLAPSED,
        headerMinWidth: TEXT_COLLAPSED_HEADER_MIN_WIDTH_PX,
        fallbackWidth: COLLAPSED_RESULT_EMPTY_WIDTH_PX,
      }).cardWidth
    }
    if (params.strategy === 'audio') {
      return Math.min(params.expandedWidthPx, 360)
    }
    return params.expandedWidthPx
  }

  switch (params.strategy) {
    case 'image':
    case 'video': {
      let mediaWidth = params.resultCarrierWidthPx
      if (mediaWidth == null) {
        const { w, h } =
          params.naturalWidth && params.naturalHeight
            ? { w: params.naturalWidth, h: params.naturalHeight }
            : ratioToNaturalSize(params.aspectRatio)
        mediaWidth = resolveCollapsedMediaPresentationSize(w, h).mediaWidth
      }
      return resolveMediaCollapsedCardWidth(mediaWidth)
    }
    case 'audio':
      return Math.min(params.expandedWidthPx, 360)
    case 'text':
    case 'structured':
    default:
      return resolveTextCollapsedCardWidth(params.expandedWidthPx, params.expandedWidthPx)
  }
}

export function resolveGenerationCardWidth(params: ResolveGenerationCardWidthParams): number {
  if (params.expanded) return params.expandedWidthPx
  return resolveCollapsedResultCardWidth(params)
}

export function expandedGenerationCardWidth(nodeType: string): number {
  return generationNodeCardWidth(nodeType)
}

/** Frame-to-media horizontal delta for geometry E2E (outer visible frame vs media rect). */
export function collapsedMediaFrameHorizontalDelta(outerWidth: number, mediaWidth: number): number {
  return Math.max(0, outerWidth - mediaWidth)
}

/** True when outer frame hugs media within border/subpixel tolerance (ImageAsset-equivalent). */
export function collapsedMediaFrameHugsMedia(
  outerWidth: number,
  mediaWidth: number,
  tolerancePx = 2,
): boolean {
  return Math.abs(outerWidth - mediaWidth) <= tolerancePx
}
